<template>
    <div>
        <!-- Documents Table -->
        <b-form-row v-if="!formDisabled" class="justify-content-md-center">
            <label class="table-title">Available Documents</label>
        </b-form-row>

        <b-form-row class="justify-content-md-center">
            <b-table
                bordered
                striped
                hover
                responsive
                :fields="documentTableFields"
                :items="documentTableItems"
            >
                <template v-slot:cell(attach)="row">
                    <b-form-radio
                        v-model.lazy="selectedUpload"
                        :disabled="formDisabled"
                        class="mr-0"
                        name="selectedFile"
                        :value="row.item"
                    />
                </template>
                <template v-slot:cell(actions)="row">
                    <b-button
                        ref="downloadButton"
                        v-b-popover.hover.auto="'Download this document.'"
                        size="sm"
                        variant="outline-tetradic-3"
                        @click.stop="downloadDocument(row.item)"
                    >
                        <fa-icon :icon="['fas', 'download']" />
                    </b-button>
                </template>
            </b-table>
            <b-col v-if="isApplicant">
                <b-button
                    size="sm"
                    :disabled="formDisabled"
                    data-toggle="tooltip"
                    data-placement="left"
                    variant="outline-tetradic-3"
                    title="Upload supporting documents"
                    @click="showUploadModal"
                >
                    <fa-icon :icon="['fas', 'upload']" />
                    Add New Document
                </b-button>
            </b-col>
        </b-form-row>
        <b-form-row v-if="!formDisabled" class="d-flex info-text justify-items-center mt-5">
            <b-col cols="4" />
            <b-col cols="4">
                <b-button
                    variant="primary"
                    class="w-100"
                    data-toggle="tooltip"
                    data-placement="left"
                    title="Save Purchase"
                    :is-loading="isSaving"
                    @click="save"
                >
                    <fa-icon :icon="['fas', 'save']" /> Save
                </b-button>
            </b-col>
        </b-form-row>
        <b-form-row v-if="!formDisabled" class="info-text">
            <b-col v-show="!validForSave" class="text-center">
                <b-form-invalid-feedback :state="validForSave" class="small">
                    * You must fill in the required fields on all tabs before you can save
                    <!-- {{ supportingDocumentValidationErrors[0] }} -->
                </b-form-invalid-feedback>
            </b-col>
        </b-form-row>

        <!-- file upload modal -->
        <fieldset :disabled="formDisabled">
            <b-modal ref="fileUploadModalRef" title="Upload File">
                <p class="my-4">
                    Click to select a file, then click the upload button
                </p>
                <div class="d-block text-center">
                    <b-form-file
                        id="upload_file"
                        ref="fileUploadRef"
                        v-model="uploadingFile"
                        choose-label="Attachment"
                    />
                </div>
                <hr>
                <p>
                    <strong>File Selected:</strong>
                    {{ uploadingFile && uploadingFile.name }}
                </p>
                <div slot="modal-footer" class="w-100">
                    <b-btn size="sm"
                           variant="secondary"
                           class="mr-2"
                           @click="hideUploadModal"
                    >
                        <fa-icon fixed-width :icon="['fal', 'arrow-from-right']" />
                        Cancel
                    </b-btn>
                    <b-btn size="sm"
                           variant="success"
                           @click="uploadFile"
                    >
                        <fa-icon fixed-width :icon="['fas', 'upload']" />
                        Upload
                    </b-btn>
                </div>
            </b-modal>
        </fieldset>
    </div>
</template>

<script>
import { mapState, mapGetters } from 'vuex';
import { Arc } from '$Shared/@arc/vue-arc';

export default {
    name: 'SupportingDocument',

    props: {
        applicationId: { type: [String, Number], required: true },
        proposalId: { type: [String, Number], required: true },
        costId: { type: [String, Number], required: true },
        purchaseId: { type: [String, Number], required: true },
    },

    data() {
        return {
            uploadingFile: null,
            isSaving: false,
        };
    },

    computed: {
        ...mapState('purchase', {
            documentList: 'documentList',
            purchaseUploadId: (state) => state.form.content.purchase_upload_id,
        }),

        ...mapGetters({
            isApplicant: 'auth/isApplicant',
            isPurchaseEditable: 'purchase/isEditable',
            // supportingDocumentValidationErrors: 'purchase/form/supportingDocumentValidationErrors',
            hasSupportingDocumentValidationErrors: 'purchase/form/hasSupportingDocumentValidationErrors',
            saveValidationErrors: 'purchase/form/saveValidationErrors',
            validForSave: 'purchase/form/validForSave',
        }),

        documentTableFields() {
            return [
                { key: 'original_file_name', label: 'File Name', sortable: true },
                {
                    key: 'uploaded_tm',
                    label: 'Upload On',
                    sortable: true,
                    formatter: (value) => {
                        const date = new Date(value);
                        return `${date.toLocaleDateString()} @ ${date.toLocaleTimeString()}`;
                    }
                },
                {
                    key: 'size',
                    label: 'Size',
                    sortable: true,
                    formatter: (value) => Arc.Numbers.bytesToHumanReadable(value),
                },
                {
                    key: 'attach',
                    label: 'Attach',
                    class: 'text-center',
                    variant: this.hasSupportingDocumentValidationErrors ? 'warning' : null
                },
                {
                    key: 'actions',
                    label: 'Actions',
                    class: 'text-center',
                },
            ];
        },

        selectedUpload: {
            get() {
                return this.documentList.find((file) => {
                    return file.purchase_upload_id === this.purchaseUploadId;
                });
            },
            set(value) {
                if (value) {
                    this.$store.dispatch('purchase/form/update', {
                        field: 'content',
                        value: { purchase_upload_id: value.purchase_upload_id }
                    });
                }
            }
        },

        documentTableItems() {
            if (!this.isApplicant && this.selectedUpload) {
                return [this.selectedUpload];
            }

            return this.documentList;
        },

        formDisabled() {
            return !this.isPurchaseEditable;
        },
    },

    async mounted() {
        await this.$store.dispatch('purchase/fetchDocumentList', {
            applicationId: this.applicationId,
            proposalId: this.proposalId
        });
    },

    methods: {
        showUploadModal() {
            this.$refs.fileUploadModalRef.show();
        },

        hideUploadModal() {
            this.$refs.fileUploadRef.reset();
            this.uploadingFile = null;
            this.$refs.fileUploadModalRef.hide();
        },

        async uploadFile() {
            await this.$store.dispatch('purchase/uploadPurchaseFile', {
                applicationId: this.applicationId,
                proposalId: this.proposalId,
                file: this.uploadingFile
            });
            await this.$store.dispatch('purchase/fetchDocumentList', {
                applicationId: this.applicationId,
                proposalId: this.proposalId
            });
            this.hideUploadModal();
        },

        async downloadDocument(fileInfo) {
            window.open(await this.$store.dispatch('purchase/getPurchaseFileUrl', {
                applicationId: this.applicationId,
                proposalId: this.proposalId,
                uploadId: fileInfo.purchase_upload_id
            }), '_blank');
        },

        save() {
            this.isSaving = true;
            let event = this.validForSave
                ? ['save:purchase:documentation']
                : ['validate:error', this.validationErrors];

            this.$emit(...event);
            this.isSaving = false;
        }
    },
};
</script>

<style lang="scss" scoped>
.table-title {
    font-weight: bold;
    font-size: 20px;
}

/deep/ .table td {
    padding: 0.25rem;
}

</style>
